/**
 * Sygnały dźwiękowe i wibracje dla akcji otwierania (szafka na klucze itp.).
 *
 * Przeglądarki (zwłaszcza iOS Safari / WKWebView) blokują dźwięk bez gestu użytkownika,
 * a po `await` na odpowiedź z API gest już wygasa. Dlatego `primeSignal()` trzeba wywołać
 * synchronicznie w handlerze kliknięcia, a `playSignal()` dopiero po odpowiedzi.
 */

type SignalType = 'opened' | 'closed' | 'error'

let audioContext: AudioContext | null = null

const getAudioContext = (): AudioContext | null => {
  if (audioContext) return audioContext
  const Ctor = window.AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try {
    audioContext = new Ctor()
  } catch {
    audioContext = null
  }
  return audioContext
}

/** Wywołać w handlerze kliknięcia - odblokowuje audio na iOS. */
export const primeSignal = () => {
  const ctx = getAudioContext()
  if (!ctx) return
  if (ctx.state === 'suspended') ctx.resume().catch(() => undefined)
  try {
    const source = ctx.createBufferSource()
    source.buffer = ctx.createBuffer(1, 1, 22050)
    source.connect(ctx.destination)
    source.start(0)
  } catch {
    // ignore
  }
}

const tone = (ctx: AudioContext, frequency: number, startAt: number, duration: number) => {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  const start = ctx.currentTime + startAt
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(frequency, start)
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(0.35, start + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  oscillator.connect(gain)
  gain.connect(ctx.destination)
  oscillator.start(start)
  oscillator.stop(start + duration + 0.05)
}

const vibrate = (pattern: number | number[]) => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(pattern)
  } catch {
    // ignore - np. iOS nie wspiera Vibration API
  }
}

export const playSignal = (type: SignalType) => {
  const ctx = getAudioContext()
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => undefined)

  try {
    if (type === 'opened') {
      if (ctx) {
        tone(ctx, 880, 0, 0.18)
        tone(ctx, 1320, 0.2, 0.3)
      }
      vibrate([150, 80, 150])
    } else if (type === 'closed') {
      if (ctx) tone(ctx, 520, 0, 0.35)
      vibrate(200)
    } else {
      if (ctx) {
        tone(ctx, 300, 0, 0.2)
        tone(ctx, 300, 0.28, 0.2)
      }
      vibrate([300, 100, 300])
    }
  } catch {
    // dźwięk to tylko dodatek - nie może wysypać UI
  }
}
